import { useQueries } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import { API_DIARY } from '@/api/endpoints';
import { DIARY_QUERY_KEY } from '@/api/queryKeys';
import { fillEmptyLessons } from '@/pages/Diary/lessons';
import type { Attachment } from '@/types/attachment';
import type {
  DiaryBehaviourEntry,
  DiaryEntry,
  DiaryEvaluationResponse,
  DiaryHomeTask,
  DiaryLesson,
  DiaryNote,
  DiaryThirdPartyEvent,
  DiaryTopic,
  DiaryWeek,
  LastModification,
} from '@/types/diary';
import { BehaviourEntryType } from '@/types/overview';
import type { VideoMeetingResponse } from '@/types/video';
import { unwrapAxiosResponse } from '@/utils/axios';
import { addDays, getWeekRangeWithin, toApiDate } from '@/utils/date';
import { toDiaryEvaluation } from '@/utils/diary';

interface TextValueResponse {
  text: string;
  attachments: Attachment[];
}

interface ScheduledTestResponse {
  description: TextValueResponse;
}

interface LessonSubjectResponse {
  value: TextValueResponse;
  isDraft?: boolean;
  creation: { authorName: string; timeModified: string };
  lastModification?: LastModification | null;
}

interface HomeTaskResponse {
  id: number;
  task: TextValueResponse;
  authorName: string;
  timeCreated: string;
  lastModification: LastModification | null;
  areAnswersEnabled: boolean;
  hasStudentAnswer: boolean;
}

interface ThirdPartyEventResponse {
  applicationId: string;
  name: string;
  url: string;
  isDeleted: boolean;
  lastModification: LastModification;
}

interface LessonResponse {
  classJournalName: string;
  roomNumber?: string | null;
  lessonNumber?: number | null;
  isOnlyForCurrentWeek: boolean;
  lessonSubjects: LessonSubjectResponse[];
  homeTasks: HomeTaskResponse[];
  thirdPartyEvents: ThirdPartyEventResponse[];
  evaluations: DiaryEvaluationResponse[];
  videoMeetings?: VideoMeetingResponse[];
  scheduledTests?: ScheduledTestResponse[];
}

interface DiaryNoteResponse {
  teacherName: string;
  text: string;
  timeCreated: string;
}

interface BehaviourEntryResponse {
  authorName: string;
  disciplineName: string;
  recordText: string;
  type?: string | null;
  timeCreated: string;
}

interface DiaryEntryResponse {
  date: string;
  isEmptyDay: boolean;
  lessons: LessonResponse[];
  diaryNotes: DiaryNoteResponse[];
  behaviourEntries: BehaviourEntryResponse[];
}

const KNOWN_BEHAVIOUR_ENTRY_TYPES: ReadonlySet<string> = new Set(
  Object.values(BehaviourEntryType),
);

const toDiaryNote = ({
  teacherName,
  text,
  timeCreated,
}: DiaryNoteResponse): DiaryNote => ({
  authorName: teacherName,
  text,
  timeCreated,
});

const toBehaviourEntry = ({
  authorName,
  disciplineName,
  recordText,
  type,
  timeCreated,
}: BehaviourEntryResponse): DiaryBehaviourEntry => ({
  authorName,
  subject: disciplineName,
  text: recordText,
  type:
    type && KNOWN_BEHAVIOUR_ENTRY_TYPES.has(type)
      ? (type as BehaviourEntryType)
      : undefined,
  timeCreated,
});

const toTopic = ({
  value,
  isDraft,
  creation,
  lastModification,
}: LessonSubjectResponse): DiaryTopic => ({
  text: value.text,
  attachments: value.attachments,
  isDraft: !!isDraft,
  authorName: creation.authorName,
  timeCreated: creation.timeModified,
  lastModification: lastModification ?? null,
});

const toHomeTask = ({
  id,
  task,
  authorName,
  timeCreated,
  lastModification,
  areAnswersEnabled,
  hasStudentAnswer,
}: HomeTaskResponse): DiaryHomeTask => ({
  id,
  text: task.text,
  attachments: task.attachments,
  authorName,
  timeCreated,
  lastModification,
  areAnswersEnabled,
  hasStudentAnswer,
});

const toThirdPartyEvent = ({
  applicationId,
  name,
  url,
  isDeleted,
  lastModification,
}: ThirdPartyEventResponse): DiaryThirdPartyEvent => ({
  applicationId,
  name,
  url,
  isDeleted,
  lastModification,
});

const toDiaryLesson = ({
  classJournalName,
  lessonNumber,
  roomNumber,
  isOnlyForCurrentWeek,
  lessonSubjects,
  homeTasks,
  thirdPartyEvents,
  evaluations,
  videoMeetings,
  scheduledTests,
}: LessonResponse): DiaryLesson => ({
  className: classJournalName,
  lessonNumber: lessonNumber ?? undefined,
  roomNumber: roomNumber ?? undefined,
  isOnlyForCurrentWeek,
  topics: lessonSubjects.map(toTopic),
  homeTasks: homeTasks.map(toHomeTask),
  thirdPartyEvents: thirdPartyEvents.map(toThirdPartyEvent),
  evaluations: evaluations.map(toDiaryEvaluation),
  videoMeetings: videoMeetings ?? [],
  scheduledTests: (scheduledTests ?? []).map(({ description }) => description),
});

const toDiaryEntry = ({
  date,
  isEmptyDay,
  lessons,
  diaryNotes,
  behaviourEntries,
}: DiaryEntryResponse): DiaryEntry => ({
  date,
  isEmptyDay,
  lessons: fillEmptyLessons(lessons.map(toDiaryLesson)),
  notes: diaryNotes.map(toDiaryNote),
  behaviourEntries: behaviourEntries.map(toBehaviourEntry),
});

export const buildDiaryQuery = (week: DiaryWeek) => {
  const fromDate = toApiDate(week.start);
  const toDate = toApiDate(week.end);

  return {
    queryKey: [...DIARY_QUERY_KEY, fromDate, toDate],
    queryFn: () =>
      apiClient
        .get<DiaryEntryResponse[]>(API_DIARY, {
          params: { from: fromDate, to: toDate },
        })
        .then(unwrapAxiosResponse)
        .then((entries) => entries.map(toDiaryEntry)),
  };
};

interface Params {
  week: DiaryWeek;
  from: Date;
  to: Date;
  shouldLoadAdjacentWeeks: boolean;
}

interface UseDiaryResult {
  weekEntries: DiaryEntry[];
  carouselEntries: DiaryEntry[];
  areEntriesLoading: boolean;
}

export const useDiary = ({
  week,
  from,
  to,
  shouldLoadAdjacentWeeks,
}: Params): UseDiaryResult => {
  const previousWeekEnd = addDays(week.start, -1);
  const nextWeekStart = addDays(week.end, 1);
  const hasPreviousWeek = shouldLoadAdjacentWeeks && previousWeekEnd >= from;
  const hasNextWeek = shouldLoadAdjacentWeeks && nextWeekStart <= to;
  const previousWeeks = hasPreviousWeek
    ? [getWeekRangeWithin({ date: previousWeekEnd, from, to })]
    : [];
  const nextWeeks = hasNextWeek
    ? [getWeekRangeWithin({ date: nextWeekStart, from, to })]
    : [];
  const weeks = [...previousWeeks, week, ...nextWeeks];
  const selectedWeekIndex = previousWeeks.length;

  const results = useQueries({ queries: weeks.map(buildDiaryQuery) });
  const selectedWeekResult = results[selectedWeekIndex];

  return {
    weekEntries: selectedWeekResult.data ?? [],
    carouselEntries: results.flatMap((result) => result.data ?? []),
    areEntriesLoading: selectedWeekResult.isLoading,
  };
};
